import React, { useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotificationStore, type Notification } from '../../store/notificationStore';
import { useAuthStore } from '../../store/authStore';
import { useStoredNotifications, useUnreadNotificationCount } from '../../hooks/useNotificationQueries';

// SECURITY: User-aware cache keys are handled by the notification hooks
import { useNotificationSync } from '../../hooks/useNotificationSync';
import { notificationApiHelpers } from '../../services/notificationApi';
import { notificationHiddenState } from '../../services/notificationHiddenState';
import NotificationPanel from './NotificationPanel';

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const { 
    unreadCount: localUnreadCount, 
    isVisible, 
    togglePanel, 
    _cleanupOldNotifications,
    // 🔥 NEW: Real-time tracking
    hasRealtimeUpdates,
    clearRealtimeUpdates
  } = useNotificationStore();
  
  const { isApiAuthReady } = useAuthStore();

  // 🔥 NEW: Use React Query to fetch stored notifications (with user-aware cache keys)
  const { 
    data: storedNotificationsData, 
    isLoading: isLoadingStored, 
    error: storedNotificationsError,
    refetch: refetchStoredNotifications
  } = useStoredNotifications({
    limit: 50,
    enabled: isApiAuthReady
  });

  // 🔥 NEW: Use React Query to fetch unread count from API
  const { 
    data: apiUnreadCount,
    refetch: refetchUnreadCount
  } = useUnreadNotificationCount(7);
  
  // 🔥 OPTIMISTIC: Track when auto-read was triggered for immediate badge clearing
  const [autoReadTriggered, setAutoReadTriggered] = React.useState(false);
  const prevLocalUnreadCountRef = React.useRef(localUnreadCount);
  
  // Reset auto-read trigger when panel closes or API count reaches 0
  useEffect(() => {
    // Only reset when panel is closed AND API count is 0 (confirmed backend sync completed)
    if (!isVisible && (apiUnreadCount?.unread_count === 0)) {
      console.log('✅ [NotificationBell] Panel closed and API count is 0 - resetting auto-read trigger');
      setAutoReadTriggered(false);
    }
    
    // 🔥 FORCE REFRESH: When panel closes after auto-read, force refresh unread count
    if (!isVisible && autoReadTriggered) {
      console.log('🔄 [NotificationBell] Panel closed after auto-read, forcing unread count refresh');
      setTimeout(() => {
        refetchUnreadCount().catch((error: any) => {
          console.error('❌ [NotificationBell] Failed to force refresh unread count after panel close:', error);
        });
      }, 1000); // Increased from 500ms to 1000ms to allow backend sync to complete
    }
  }, [isVisible, apiUnreadCount?.unread_count, autoReadTriggered, refetchUnreadCount]);
  
  // 🔥 RESET AUTO-READ: When local count increases (new notifications), reset auto-read trigger
  useEffect(() => {
    const prevCount = prevLocalUnreadCountRef.current;
    
    // Only reset if count actually increased AND auto-read is currently triggered
    if (localUnreadCount > prevCount && localUnreadCount > 0 && autoReadTriggered) {
      console.log(`🔔 [NotificationBell] New notifications detected (${prevCount} → ${localUnreadCount}), resetting auto-read trigger`);
      setAutoReadTriggered(false);
    }
    
    prevLocalUnreadCountRef.current = localUnreadCount;
  }, [localUnreadCount]); // Minimal dependencies
  
  // Set auto-read trigger when panel opens with unread count
  useEffect(() => {
    if (isVisible && (apiUnreadCount?.unread_count || 0) > 0 && !autoReadTriggered) {
      console.log('🚀 [NotificationBell] Setting auto-read trigger for optimistic badge clearing');
      setAutoReadTriggered(true);
    }
  }, [isVisible, apiUnreadCount?.unread_count, autoReadTriggered]);
  
  // 🔥 EVENT-DRIVEN: Prioritize local count for immediate SSE updates, with API as fallback
  const unreadCount = useMemo(() => {
    // If auto-read was triggered, show 0 immediately (optimistic)
    if (autoReadTriggered) {
      return 0;
    }
    
    const apiCount = apiUnreadCount?.unread_count ?? 0;
    const localCount = localUnreadCount ?? 0;
    
    // 🔥 ENHANCED EVENT-DRIVEN PRIORITY:
    // 1. If we have real-time updates, always prioritize local count (immediate SSE)
    // 2. If local count > 0, use it (covers SSE notifications)
    // 3. If local count is 0 but API count > 0, use API (stored notifications)
    // 4. If both are 0, show 0
    
    if (hasRealtimeUpdates && localCount > 0) {
      console.log(`� [NotificationBell] Using local count (${localCount}) - real-time SSE updates detected`);
      return localCount;
    }
    
    if (localCount > 0) {
      console.log(`🔔 [NotificationBell] Using local count (${localCount}) - local notifications`);
      return localCount;
    }
    
    if (apiCount > 0) {
      console.log(`� [NotificationBell] Using API count (${apiCount}) - stored notifications`);
      return apiCount;
    }
    
    // Both counts are 0
    return 0;
  }, [apiUnreadCount?.unread_count, localUnreadCount, autoReadTriggered, hasRealtimeUpdates]);

  // 🔥 NEW: Auto-refresh API count when panel is opened (faster badge updates)
  useEffect(() => {
    if (isVisible && apiUnreadCount?.unread_count && apiUnreadCount.unread_count > 0) {
      console.log('🔄 [NotificationBell] Panel opened with API unread count > 0, refreshing count in 5 seconds');
      const refreshTimer = setTimeout(() => {
        refetchUnreadCount().catch((error: any) => {
          console.error('❌ [NotificationBell] Failed to refresh unread count after panel open:', error);
        });
      }, 5000); // Increased from 3 to 5 seconds to allow backend sync to complete
      
      return () => clearTimeout(refreshTimer);
    }
  }, [isVisible, apiUnreadCount?.unread_count, refetchUnreadCount]);

  // 🔥 NEW: Enable periodic notification sync
  const { manualSync, isStale } = useNotificationSync({
    enabled: isApiAuthReady,
    syncInterval: 10 * 60 * 1000, // 🔥 FIXED: Increase to 10 minutes to reduce API calls (was 5 minutes)
    syncOnFocus: false, // 🔥 FIXED: Disable sync on focus to reduce duplicate calls
    syncOnReconnect: true
  });

  // 🔥 OPTIMIZED: Track last integrated batch to prevent re-processing
  const lastIntegratedBatchRef = React.useRef<string>('');
  
  // 🔥 NEW: Integrate stored notifications into the local store when they're fetched (BATCH MODE)
  useEffect(() => {
    if (!storedNotificationsData?.notifications || storedNotificationsData.notifications.length === 0) {
      return;
    }
    
    // Create a stable hash of notification IDs to detect if this is a new batch
    const batchHash = storedNotificationsData.notifications
      .map(n => n.id)
      .sort()
      .join(',');
    
    // Skip if we've already processed this exact batch
    if (lastIntegratedBatchRef.current === batchHash) {
      console.log(`� [NotificationBell] Skipping re-integration of same batch (${storedNotificationsData.notifications.length} notifications)`);
      return;
    }
    
    console.log(`🔔 [NotificationBell] Processing batch of ${storedNotificationsData.notifications.length} stored notifications`);
    
    // Get persistently hidden notification IDs to filter them out
    const hiddenIds = notificationHiddenState.getHiddenIds();
    const { isDuplicateOfSSEEvent, isRecoveryNotification, integrateStoredNotificationsBatch } = useNotificationStore.getState();
    
    let skippedHidden = 0;
    let skippedDuplicate = 0;
    let skippedNonRecovery = 0;
    let skippedSnapshot = 0;
    
    // Filter and convert notifications in a single pass
    const notificationsToIntegrate: Notification[] = [];
    
    for (const stored of storedNotificationsData.notifications) {
      try {
        // Skip if notification is persistently hidden
        if (hiddenIds.has(stored.id)) {
          skippedHidden++;
          continue;
        }
        
        // 🔥 SAFETY: Skip position_snapshot events
        if (stored.event_type === 'position_snapshot') {
          skippedSnapshot++;
          continue;
        }
        
        // Skip if this is a duplicate of a recent SSE event (within 30 minutes)
        if (isDuplicateOfSSEEvent(stored)) {
          skippedDuplicate++;
          continue;
        }
        
        // Skip non-recovery events (events that happened during current session)
        if (!isRecoveryNotification(stored)) {
          skippedNonRecovery++;
          continue;
        }
        
        // Convert to frontend format
        const frontendNotification = notificationApiHelpers.convertStoredToFrontend(stored);
        notificationsToIntegrate.push(frontendNotification);
        
      } catch (error) {
        console.warn('[NotificationBell] Failed to convert stored notification:', error, stored);
      }
    }
    
    // Batch integrate all valid notifications in a SINGLE store update
    if (notificationsToIntegrate.length > 0) {
      const result = integrateStoredNotificationsBatch(notificationsToIntegrate);
      console.log(`✅ [NotificationBell] Batch integration complete: ${result.added} added, ${result.skipped} already existed`);
    }
    
    // Log filtering stats
    const totalSkipped = skippedHidden + skippedDuplicate + skippedNonRecovery + skippedSnapshot;
    if (totalSkipped > 0) {
      console.log(`🔔 [NotificationBell] Filtered out ${totalSkipped} notifications:`, {
        hidden: skippedHidden,
        duplicate: skippedDuplicate,
        nonRecovery: skippedNonRecovery,
        snapshot: skippedSnapshot
      });
    }
    
    // Mark this batch as processed
    lastIntegratedBatchRef.current = batchHash;
    
  }, [storedNotificationsData]); // Removed integrateStoredNotification from deps - we get it from getState()

  // 🔥 NEW: Log API unread count vs local unread count for debugging
  useEffect(() => {
    if (apiUnreadCount?.unread_count !== undefined) {
      console.log(`🔢 [NotificationBell] API unread count: ${apiUnreadCount.unread_count}, Local unread count: ${unreadCount}`);
      
      // Log sync status
      if (isStale()) {
        console.log('📅 [NotificationBell] Notifications are stale - sync recommended');
      }
    }
  }, [apiUnreadCount, unreadCount, isStale]);

  // Cleanup old notifications on mount and periodically
  useEffect(() => {
    _cleanupOldNotifications();
    
    // Cleanup every 5 minutes
    const interval = setInterval(_cleanupOldNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [_cleanupOldNotifications]);

  // 🔥 NEW: Periodic sync for stored notifications
  useEffect(() => {
    if (!isApiAuthReady) return;

    // Sync stored notifications every 1 minute for better responsiveness
    const syncInterval = setInterval(() => {
      console.log('🔄 [NotificationBell] Periodic sync of stored notifications');
      refetchStoredNotifications().catch((error: any) => {
        console.error('❌ [NotificationBell] Periodic sync failed:', error);
      });
    }, 1 * 60 * 1000);

    return () => clearInterval(syncInterval);
  }, [isApiAuthReady, refetchStoredNotifications]);
  
  // 🔥 NEW: Clear real-time updates when panel is opened
  useEffect(() => {
    if (isVisible && hasRealtimeUpdates) {
      console.log('🔔 [NotificationBell] Panel opened - clearing real-time update flags');
      clearRealtimeUpdates();
    }
  }, [isVisible, hasRealtimeUpdates, clearRealtimeUpdates]);

  // Animation trigger for new notifications with robust cleanup
  const [shouldShake, setShouldShake] = React.useState(false);
  const [forceRotationReset, setForceRotationReset] = React.useState(0);
  const prevUnreadCount = React.useRef(unreadCount);
  const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      // Clear any existing animation timeout to prevent overlapping animations
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      
      setShouldShake(true);
      animationTimeoutRef.current = setTimeout(() => {
        setShouldShake(false);
        setForceRotationReset(prev => prev + 1); // Force reset if needed
      }, 650); // Slightly longer than animation duration to ensure completion
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  // Cleanup animation timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // Handle manual sync on double-click and reset animation on any click
  // 🔧 FIX: Click handler now resets stuck animation by explicitly setting shouldShake to false
  const handleClick = useCallback(() => {
    // Reset animation if it's stuck
    if (shouldShake) {
      setShouldShake(false);
      setForceRotationReset(prev => prev + 1);
    }
    togglePanel();
  }, [shouldShake, togglePanel]);

  const handleDoubleClick = useCallback(async () => {
    console.log('🔄 [NotificationBell] Manual sync triggered');
    try {
      await manualSync();
      // Show a brief visual feedback
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 300);
    } catch (error) {
      console.error('❌ [NotificationBell] Manual sync failed:', error);
    }
  }, [manualSync]);

  return (
    <div className={`relative ${className}`} data-notification-bell-container>
      {/* Clickable wrapper that includes the entire bell area */}
      <motion.button
        data-notification-bell
        className={`relative bg-white/10 backdrop-blur-sm p-2.5 rounded-xl border border-white/10 transition-all duration-300 ease-in-out transform hover:scale-105 touch-manipulation ${
          isVisible 
            ? 'text-blue-400 bg-blue-500/20 border-blue-400/20' 
            : 'text-white/70 hover:text-white hover:bg-white/20'
        } ${isLoadingStored ? 'animate-pulse' : ''}`}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}${isLoadingStored ? ' (loading...)' : ''}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        // 🔧 FIX: Explicit rotation reset prevents stuck animation bug
        animate={shouldShake ? { 
          rotate: [0, -10, 10, -10, 10, 0],
          transition: { duration: 0.6 }
        } : {
          rotate: 0, // Explicit reset to prevent stuck rotation
          transition: { duration: 0.1 }
        }}
        key={forceRotationReset} // Force re-render if rotation gets stuck
        style={{ minWidth: '44px', minHeight: '44px' }} // Ensure touch target size
      >
        <BellIcon className="w-5 h-5" />
        
        {/* 🔥 NEW: Loading indicator for stored notifications */}
        {isLoadingStored && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping" />
          </div>
        )}
        
        {/* 🔥 NEW: Error indicator */}
        {storedNotificationsError && (
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white/20" 
               title={`Error loading notifications: ${(storedNotificationsError as any)?.message || storedNotificationsError}`} />
        )}
        
        {/* Notification Badge - now inside the clickable button */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1.5 font-semibold shadow-lg border border-white/20"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                transition: { 
                  type: 'spring', 
                  stiffness: 300, 
                  damping: 20 
                }
              }}
              exit={{ scale: 0, opacity: 0 }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Badge Pulse Animation for New Notifications - also inside the button */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              className="absolute -top-1 -right-1 bg-red-400 rounded-full min-w-5 h-5 pointer-events-none"
              initial={{ scale: 1, opacity: 0.6 }}
              animate={{ 
                scale: [1, 1.4, 1], 
                opacity: [0.6, 0, 0.6],
                transition: { 
                  duration: 2, 
                  repeat: Infinity,
                  ease: 'easeInOut'
                }
              }}
              exit={{ opacity: 0 }}
            />
          )}
        </AnimatePresence>
      </motion.button>

      {/* Notification Panel */}
      <NotificationPanel />
    </div>
  );
};

export default React.memo(NotificationBell);